import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Card,
  CardContent,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  InputAdornment,
  Tabs,
  Tab,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  Upload as UploadIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
} from 'recharts';
import { ru } from 'date-fns/locale';
import { expensesApi, holdersApi } from '../services/api';
import { formatCurrency, formatDate, getCountryLabel } from '../utils/formatters';
import { useApp } from '../App';

const CATEGORY_COLORS = {
  'RE Обслуживание': '#E74C3C',
  'RE Аренда': '#9B59B6',
  'Транспорт': '#3498DB',
  'Учеба': '#1ABC9C',
  'Путешествия': '#F39C12',
  'Зарплаты': '#2ECC71',
  'Компании': '#E67E22',
  'Здоровье': '#27AE60',
  'Другое': '#95A5A6',
};

const Expenses = () => {
  const { showNotification } = useApp();
  const [loading, setLoading] = useState(true);
  const [expenses, setExpenses] = useState([]);
  const [holders, setHolders] = useState([]);
  const [categories, setCategories] = useState({});
  const [summary, setSummary] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editExpense, setEditExpense] = useState(null);
  const [tabValue, setTabValue] = useState(0);

  // Filters
  const [filters, setFilters] = useState({
    holder: '',
    category: '',
    startDate: null,
    endDate: null,
    country: 'All',
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [expensesRes, holdersRes, categoriesRes, summaryRes] = await Promise.all([
        expensesApi.getAll({
          holder: filters.holder || undefined,
          category: filters.category || undefined,
          startDate: filters.startDate?.toISOString().split('T')[0],
          endDate: filters.endDate?.toISOString().split('T')[0],
          country: filters.country !== 'All' ? filters.country : undefined,
        }),
        holdersApi.getAll(),
        expensesApi.getCategories(),
        expensesApi.getSummary({
          startDate: filters.startDate?.toISOString().split('T')[0],
          endDate: filters.endDate?.toISOString().split('T')[0],
        }),
      ]);

      setExpenses(expensesRes.data);
      setHolders(holdersRes.data.filter((h) => h.type === 'INDIVIDUAL'));
      setCategories(categoriesRes.data);
      setSummary(summaryRes.data);
    } catch (error) {
      showNotification('Ошибка загрузки расходов', 'error');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [filters, showNotification]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleOpenDialog = (expense = null) => {
    setEditExpense(
      expense || {
        date: new Date().toISOString().split('T')[0],
        category: '',
        subcategory: '',
        amount: '',
        currency: 'RUB',
        holderId: '',
        country: 'RU',
        description: '',
      }
    );
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditExpense(null);
  };

  const handleSaveExpense = async () => {
    try {
      const data = {
        ...editExpense,
        amount: parseFloat(editExpense.amount),
      };

      if (editExpense.id) {
        await expensesApi.update(editExpense.id, data);
        showNotification('Расход обновлен', 'success');
      } else {
        await expensesApi.create(data);
        showNotification('Расход добавлен', 'success');
      }

      handleCloseDialog();
      fetchData();
    } catch (error) {
      showNotification(error.response?.data?.error || 'Ошибка сохранения', 'error');
    }
  };

  const handleDeleteExpense = async (id) => {
    if (!window.confirm('Удалить расход?')) return;

    try {
      await expensesApi.delete(id);
      showNotification('Расход удален', 'success');
      fetchData();
    } catch (error) {
      showNotification('Ошибка удаления', 'error');
    }
  };

  // Prepare chart data
  const categoryChartData = summary?.byCategory
    ? Object.entries(summary.byCategory).map(([name, data]) => ({
        name,
        value: Math.round(data.total),
        color: CATEGORY_COLORS[name] || '#95A5A6',
      }))
    : [];

  const monthlyChartData = summary?.byMonth
    ? Object.entries(summary.byMonth)
        .sort((a, b) => a[0].localeCompare(b[0]))
        .map(([month, value]) => ({
          month: month.substring(5), // Show only MM
          value: Math.round(value),
        }))
    : [];

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ru}>
      <Box>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" fontWeight={600}>
            Расходы
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="outlined" startIcon={<UploadIcon />}>
              Импорт
            </Button>
            <Button variant="outlined" startIcon={<DownloadIcon />}>
              Экспорт
            </Button>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}>
              Добавить расход
            </Button>
          </Box>
        </Box>

        {/* Tabs */}
        <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} sx={{ mb: 3 }}>
          <Tab label="Список расходов" />
          <Tab label="Аналитика" />
        </Tabs>

        {tabValue === 0 && (
          <>
            {/* Filters */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={2}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Владелец</InputLabel>
                      <Select
                        value={filters.holder}
                        label="Владелец"
                        onChange={(e) => handleFilterChange('holder', e.target.value)}
                      >
                        <MenuItem value="">Все</MenuItem>
                        {holders.map((h) => (
                          <MenuItem key={h.id} value={h.code}>
                            {h.code}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Категория</InputLabel>
                      <Select
                        value={filters.category}
                        label="Категория"
                        onChange={(e) => handleFilterChange('category', e.target.value)}
                      >
                        <MenuItem value="">Все</MenuItem>
                        {Object.keys(categories).map((cat) => (
                          <MenuItem key={cat} value={cat}>
                            {cat}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <DatePicker
                      label="С даты"
                      value={filters.startDate}
                      onChange={(date) => handleFilterChange('startDate', date)}
                      slotProps={{ textField: { size: 'small', fullWidth: true } }}
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <DatePicker
                      label="По дату"
                      value={filters.endDate}
                      onChange={(date) => handleFilterChange('endDate', date)}
                      slotProps={{ textField: { size: 'small', fullWidth: true } }}
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Страна</InputLabel>
                      <Select
                        value={filters.country}
                        label="Страна"
                        onChange={(e) => handleFilterChange('country', e.target.value)}
                      >
                        <MenuItem value="All">Все</MenuItem>
                        <MenuItem value="RU">Россия</MenuItem>
                        <MenuItem value="UAE">ОАЭ</MenuItem>
                        <MenuItem value="Other">Другое</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      <Typography variant="h6" color="primary">
                        {formatCurrency(summary?.total || 0, 'USD')}
                      </Typography>
                      <Tooltip title="Обновить">
                        <IconButton onClick={fetchData} size="small">
                          <RefreshIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Expenses Table */}
            <Card>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Дата</TableCell>
                      <TableCell>Владелец</TableCell>
                      <TableCell>Категория</TableCell>
                      <TableCell>Подкатегория</TableCell>
                      <TableCell align="right">Сумма</TableCell>
                      <TableCell align="right">Сумма (USD)</TableCell>
                      <TableCell>Страна</TableCell>
                      <TableCell>Описание</TableCell>
                      <TableCell width={100}>Действия</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell colSpan={9} align="center">
                          Загрузка...
                        </TableCell>
                      </TableRow>
                    ) : expenses.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={9} align="center">
                          Нет данных
                        </TableCell>
                      </TableRow>
                    ) : (
                      expenses.map((expense) => (
                        <TableRow key={expense.id} hover>
                          <TableCell>{formatDate(expense.date)}</TableCell>
                          <TableCell>
                            <Chip label={expense.Holder?.code || '-'} size="small" />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={expense.category}
                              size="small"
                              sx={{
                                bgcolor: CATEGORY_COLORS[expense.category] || '#95A5A6',
                                color: 'white',
                              }}
                            />
                          </TableCell>
                          <TableCell>{expense.subcategory || '-'}</TableCell>
                          <TableCell align="right">
                            {expense.amount?.toLocaleString()} {expense.currency}
                          </TableCell>
                          <TableCell align="right">
                            {formatCurrency(expense.amountUSD, 'USD')}
                          </TableCell>
                          <TableCell>{getCountryLabel(expense.country)}</TableCell>
                          <TableCell>
                            <Tooltip title={expense.description || ''}>
                              <Typography
                                variant="body2"
                                noWrap
                                sx={{ maxWidth: 150 }}
                              >
                                {expense.description || '-'}
                              </Typography>
                            </Tooltip>
                          </TableCell>
                          <TableCell>
                            <IconButton
                              size="small"
                              onClick={() => handleOpenDialog(expense)}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeleteExpense(expense.id)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Card>
          </>
        )}

        {tabValue === 1 && (
          <Grid container spacing={3}>
            {/* Category Pie Chart */}
            <Grid item xs={12} md={6}>
              <Card sx={{ height: 400 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Распределение по категориям
                  </Typography>
                  <ResponsiveContainer width="100%" height={320}>
                    <PieChart>
                      <Pie
                        data={categoryChartData}
                        cx="50%"
                        cy="50%"
                        outerRadius={120}
                        dataKey="value"
                        label={({ name, percent }) =>
                          `${name}: ${(percent * 100).toFixed(0)}%`
                        }
                        labelLine={false}
                      >
                        {categoryChartData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <RechartsTooltip
                        formatter={(value) => formatCurrency(value, 'USD')}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </Grid>

            {/* Monthly Bar Chart */}
            <Grid item xs={12} md={6}>
              <Card sx={{ height: 400 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Расходы по месяцам
                  </Typography>
                  <ResponsiveContainer width="100%" height={320}>
                    <BarChart data={monthlyChartData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis tickFormatter={(v) => `$${(v / 1000).toFixed(0)}K`} />
                      <RechartsTooltip
                        formatter={(value) => formatCurrency(value, 'USD')}
                      />
                      <Bar dataKey="value" fill="#1E3A5F" />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </Grid>

            {/* Category Breakdown Table */}
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Детализация по категориям
                  </Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Категория</TableCell>
                          <TableCell align="right">Сумма (USD)</TableCell>
                          <TableCell align="right">Доля</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {categoryChartData.map((cat) => (
                          <TableRow key={cat.name}>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box
                                  sx={{
                                    width: 12,
                                    height: 12,
                                    borderRadius: '50%',
                                    bgcolor: cat.color,
                                  }}
                                />
                                {cat.name}
                              </Box>
                            </TableCell>
                            <TableCell align="right">
                              {formatCurrency(cat.value, 'USD')}
                            </TableCell>
                            <TableCell align="right">
                              {summary?.total
                                ? `${((cat.value / summary.total) * 100).toFixed(1)}%`
                                : '-'}
                            </TableCell>
                          </TableRow>
                        ))}
                        <TableRow>
                          <TableCell>
                            <strong>Итого</strong>
                          </TableCell>
                          <TableCell align="right">
                            <strong>{formatCurrency(summary?.total || 0, 'USD')}</strong>
                          </TableCell>
                          <TableCell align="right">
                            <strong>100%</strong>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {/* Add/Edit Dialog */}
        <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
          <DialogTitle>
            {editExpense?.id ? 'Редактировать расход' : 'Добавить расход'}
          </DialogTitle>
          <DialogContent dividers>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <DatePicker
                  label="Дата"
                  value={editExpense?.date ? new Date(editExpense.date) : null}
                  onChange={(date) =>
                    setEditExpense({
                      ...editExpense,
                      date: date?.toISOString().split('T')[0],
                    })
                  }
                  slotProps={{ textField: { fullWidth: true, required: true } }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth required>
                  <InputLabel>Владелец</InputLabel>
                  <Select
                    value={editExpense?.holderId || ''}
                    label="Владелец"
                    onChange={(e) =>
                      setEditExpense({ ...editExpense, holderId: e.target.value })
                    }
                  >
                    {holders.map((h) => (
                      <MenuItem key={h.id} value={h.id}>
                        {h.code} - {h.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth required>
                  <InputLabel>Категория</InputLabel>
                  <Select
                    value={editExpense?.category || ''}
                    label="Категория"
                    onChange={(e) =>
                      setEditExpense({
                        ...editExpense,
                        category: e.target.value,
                        subcategory: '',
                      })
                    }
                  >
                    {Object.keys(categories).map((cat) => (
                      <MenuItem key={cat} value={cat}>
                        {cat}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Подкатегория</InputLabel>
                  <Select
                    value={editExpense?.subcategory || ''}
                    label="Подкатегория"
                    onChange={(e) =>
                      setEditExpense({ ...editExpense, subcategory: e.target.value })
                    }
                    disabled={!editExpense?.category}
                  >
                    <MenuItem value="">Не указана</MenuItem>
                    {(categories[editExpense?.category] || []).map((sub) => (
                      <MenuItem key={sub} value={sub}>
                        {sub}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  required
                  label="Сумма"
                  type="number"
                  value={editExpense?.amount || ''}
                  onChange={(e) =>
                    setEditExpense({ ...editExpense, amount: e.target.value })
                  }
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Валюта</InputLabel>
                  <Select
                    value={editExpense?.currency || 'RUB'}
                    label="Валюта"
                    onChange={(e) =>
                      setEditExpense({ ...editExpense, currency: e.target.value })
                    }
                  >
                    {['RUB', 'USD', 'EUR', 'AED'].map((c) => (
                      <MenuItem key={c} value={c}>
                        {c}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Страна</InputLabel>
                  <Select
                    value={editExpense?.country || 'RU'}
                    label="Страна"
                    onChange={(e) =>
                      setEditExpense({ ...editExpense, country: e.target.value })
                    }
                  >
                    <MenuItem value="RU">Россия</MenuItem>
                    <MenuItem value="UAE">ОАЭ</MenuItem>
                    <MenuItem value="Other">Другое</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  label="Описание"
                  value={editExpense?.description || ''}
                  onChange={(e) =>
                    setEditExpense({ ...editExpense, description: e.target.value })
                  }
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Отмена</Button>
            <Button
              variant="outlined"
              onClick={() => {
                handleSaveExpense();
                handleOpenDialog();
              }}
            >
              Сохранить и создать еще
            </Button>
            <Button variant="contained" onClick={handleSaveExpense}>
              Сохранить
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default Expenses;
